import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  TextField,
  TextArea,
  IconButton,
} from '@radix-ui/themes';
import { ListBulletIcon, ArrowLeftIcon } from '@radix-ui/react-icons';
import { useCreateListMutation } from '../../graphql/mutations/CreateList.generated';
import { GetListsDocument } from '../../graphql/queries/GetLists.generated';

const ListCreate: React.FC = () => {
  const navigate = useNavigate();
  const [createList, { loading }] = useCreateListMutation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canCreate = name.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      const result = await createList({
        variables: {
          input: {
            name: name.trim(),
            description: description.trim() || undefined,
          },
        },
        refetchQueries: [{ query: GetListsDocument, variables: { first: 50 } }],
      });

      if (result.data?.createList) {
        navigate(`/lists/${result.data.createList.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create list');
    }
  };

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" gap="3">
          <IconButton
            variant="ghost"
            color="gray"
            onClick={() => navigate('/lists')}
          >
            <ArrowLeftIcon width={20} height={20} />
          </IconButton>
          <Box
            style={{
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: 'var(--blue-3)',
              color: 'var(--blue-11)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ListBulletIcon width={24} height={24} />
          </Box>
          <Box>
            <Heading as="h1" size="5" weight="bold">
              Create List
            </Heading>
            <Text size="2" color="gray">
              Organize addresses into a list for tracking and automation
            </Text>
          </Box>
        </Flex>
      </Box>

      <Card size="2">
        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Box>
              <Text as="label" size="2" weight="medium" mb="1">
                Name
              </Text>
              <TextField.Root
                placeholder="Enter list name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Box>

            <Box>
              <Text as="label" size="2" weight="medium" mb="1">
                Description
              </Text>
              <TextArea
                placeholder="Optional description for this list"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </Box>

            {error && (
              <Text color="red" size="2">
                {error}
              </Text>
            )}

            <Flex gap="3" justify="end" mt="2">
              <Button
                type="button"
                variant="soft"
                color="gray"
                onClick={() => navigate('/lists')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !canCreate}>
                {loading ? 'Creating...' : 'Create List'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Card>
    </Box>
  );
};

export default ListCreate;
